// Curved screens and curved screen walls (CIN-15402). Pure; no imports.
//
// A curve is the circular arc through the center and both ends of a chord `2 * halfChord` wide whose ends stand
// `sagitta` in front of its center: concave toward the audience. Units are the caller's (meters in 3D). The center
// stays where a flat screen's face is, so sightlines that aim at the screen's center are unchanged.
//
// DECISIONS
// - The chord is the object's width: the footprint, the plan and every check keep measuring the box; only the arc
//   comes forward of it. The picture is laid out along the ARC (u is arc length), so its aspect is arc / height.
// - A sagitta deeper than the half chord would be more than a semicircle: it is clamped to the semicircle.

export type CurvedPanelBuffers = {
    positions: Float32Array;
    normals: Float32Array;
    uvs: Float32Array;
    indices: Uint32Array;
};

const FLAT = 1e-6;

const clampSagitta = (halfChord: number, sagitta: number): number => Math.min(Math.max(0, sagitta), halfChord);

/** Radius of the arc; Infinity when flat. */
export function curveRadius(halfChord: number, sagitta: number): number {
    const s = clampSagitta(halfChord, sagitta);
    return s < FLAT ? Infinity : (halfChord * halfChord + s * s) / (2 * s);
}

/** How far the arc stands in front of its center at `x` from the center; beyond the ends it keeps to the circle. */
export function curveDepthAt(x: number, halfChord: number, sagitta: number): number {
    const r = curveRadius(halfChord, sagitta);
    if (!Number.isFinite(r)) return 0;
    const ax = Math.min(Math.abs(x), r);
    return r - Math.sqrt(r * r - ax * ax);
}

/** Half the angle the arc sweeps: how far its right end is turned toward the audience, radians. */
export function curveEndAngle(halfChord: number, sagitta: number): number {
    const r = curveRadius(halfChord, sagitta);
    return Number.isFinite(r) ? Math.asin(Math.min(1, halfChord / r)) : 0;
}

/** Length of the arc (the chord when flat). */
export function curveArcLength(halfChord: number, sagitta: number): number {
    const r = curveRadius(halfChord, sagitta);
    return Number.isFinite(r) ? 2 * r * curveEndAngle(halfChord, sagitta) : 2 * halfChord;
}

/**
 * A `width` (chord) x `height` panel bent into the arc of `sagitta`: centered on the origin, its center at z = 0
 * facing +z, its ends at z = sagitta. `u` runs 0..1 along the arc from the left end, `v` 0..1 from the bottom, so a
 * texture fitted to `curveArcLength / height` keeps its aspect.
 */
export function curvedPanelBuffers(width: number, height: number, sagitta: number, segments = 48): CurvedPanelBuffers {
    const half = width / 2;
    const r = curveRadius(half, sagitta);
    const end = curveEndAngle(half, sagitta);
    const count = Number.isFinite(r) ? Math.max(2, segments) : 1;
    const positions = new Float32Array((count + 1) * 2 * 3);
    const normals = new Float32Array((count + 1) * 2 * 3);
    const uvs = new Float32Array((count + 1) * 2 * 2);
    for (let i = 0; i <= count; i += 1) {
        const u = i / count;
        const angle = -end + 2 * end * u;
        const x = Number.isFinite(r) ? r * Math.sin(angle) : -half + width * u;
        const z = Number.isFinite(r) ? r - r * Math.cos(angle) : 0;
        for (let row = 0; row < 2; row += 1) {
            const k = i * 2 + row;
            positions.set([x, row === 0 ? -height / 2 : height / 2, z], k * 3);
            normals.set([-Math.sin(angle), 0, Math.cos(angle)], k * 3);
            uvs.set([u, row], k * 2);
        }
    }
    const indices = new Uint32Array(count * 6);
    for (let i = 0; i < count; i += 1) {
        const b0 = i * 2;
        const b1 = b0 + 2;
        // Counter-clockwise seen from +z: bottom left, bottom right, top right / bottom left, top right, top left.
        indices.set([b0, b1, b1 + 1, b0, b1 + 1, b0 + 1], i * 6);
    }
    return { positions, normals, uvs, indices };
}
